import React, { useState, useEffect } from 'react';
import {TextField, Button, MenuItem, DialogTitle, DialogContent, Box} from '@mui/material';
import CommonDialog from '../CommonDialog/CommonDialog';
import { getLibraryByUserId } from '../../services/libraryService';
import { getCellsByLibIdAndViewType } from '../../services/cellService';
import { getViewsByCellId, getViewsByViewType } from '../../services/viewService';
import { PREDEFINED_LIBRARIES, VIEW_TYPES } from "../../constants/Constants";


const AddInstanceDialog = ({ open, onClose, onCellViewSelect, instanceName, onInstanceRename, 
                              isEdit ,editInstanceName, instancesMapRef, viewTypeId, activeTabCellId,
                              activeTab, getNextInstanceName: getScopedNextInstanceName,
                              validateInstanceName, onInstanceNameValidity ,onHide}) => {
  const viewType = (viewTypeId === VIEW_TYPES.schematic) ? VIEW_TYPES.symbol : viewTypeId;

  const [libraries, setLibraries] = useState([]);
  const [cellsList, setCellsList] = useState([]);
  const [viewsList, setViewsList] = useState([]);
  const [library, setLibrary] = useState('');
  const [selectedCell, setSelectedCell] = useState('');;
  const [popupIstanceName, setPopupInstanceName] = useState('');
  const [selectedView, setSelectedView] = useState(null);
  const [cells, setCells] = useState(null);
  const [instanceNameError, setInstanceNameError] = useState('');

  const getCurrentInstancesMap = () => {
    if (!instancesMapRef?.current) return {};
    return activeTab ? (instancesMapRef.current[activeTab] || {}) : instancesMapRef.current;
  };

  const normalizeInstanceName = (name) => (name || '').trim().toLowerCase();

  const isReservedInstanceName = (name) => (name || '').trim() === 'no-conn';

  const isInstanceNameValid = (name) => {
    const trimmed = (name || '').trim();
    if (!trimmed || isReservedInstanceName(trimmed)) return true;
    if (validateInstanceName) return validateInstanceName(trimmed);
    return !Object.keys(getCurrentInstancesMap()).some((existingName) => (
      normalizeInstanceName(existingName) === normalizeInstanceName(trimmed) &&
      normalizeInstanceName(existingName) !== normalizeInstanceName(editInstanceName)
    ));
  };

  const setInstanceNameWithValidation = (value) => {
    // Empty check first
    if (!value.trim()) {
      setPopupInstanceName(value);
      setInstanceNameError('Instance name cannot be empty');
      onInstanceNameValidity?.(false);
      onInstanceRename?.(value, { isValid: false });
      return;
    }
    const valid = isInstanceNameValid(value);
    setPopupInstanceName(value);
    setInstanceNameError(valid ? '' : 'Instance name already exists in this view');
    onInstanceNameValidity?.(valid);
    onInstanceRename?.(value, { isValid: valid });
  };

  useEffect(() => {
    if (viewTypeId == VIEW_TYPES.schematic) {
      setCells([
        { cell: 'nmos', path: '/editors/images/toolbox/nmos.svg' },
        { cell: 'pmos', path: '/editors/images/toolbox/pmos.svg' },
        { cell: 'symbol', path: '/editors/images/toolbox/symbol.svg' },
        { cell: 'no-conn', path: '/editors/images/toolbox/no-conn.svg' }
      ]);
    }
    else if (viewTypeId == VIEW_TYPES.stick) {
      setCells([
        { cell: 'nmos', path: '/editors/images/toolbox/nmos-stick-diagram.svg' },
        { cell: 'pmos', path: '/editors/images/toolbox/pmos-stick-diagram.svg' },
        { cell: 'no-conn', path: '/editors/images/toolbox/no-conn.svg' }
      ]);
    }
  }, []);

  const getNextInstanceName = () => {
    if (getScopedNextInstanceName) {
      const selectedCellName = cellsList.find(c => c.cell_id === selectedCell)?.name || 'i';
      return getScopedNextInstanceName(selectedCellName);
    }
    const existingNames = Object.keys(getCurrentInstancesMap());
    let max = 0;
    existingNames.forEach(name => {
      const match = name.match(/^i(\d+)$/);
      if (match) {
        max = Math.max(max, parseInt(match[1]));
      }
    });
    return `i${max + 1}`;
  };

  useEffect(() => {
    const currentInstances = getCurrentInstancesMap();
    if (isEdit && editInstanceName && currentInstances?.[editInstanceName] && libraries.length > 0) {
      setInstanceNameWithValidation(instanceName);
      const instance = currentInstances[editInstanceName];
      // Parse cell type from shapeType string like: shape=nmos4;labelPosition=right;
      const shapeType = instance.shapeType?.split(';')?.[0]?.split('=')?.[1] || '';
      setSelectedCell(shapeType);
      setLibrary(instance.libraryId || instance.library);
    }
  }, [isEdit, editInstanceName, instanceName, instancesMapRef, open, activeTab, libraries.length]);

  // After each placement the parent updates instanceName prop; refresh the field if a cell is already selected
  useEffect(() => {
    if (!isEdit && selectedCell && cellsList.length > 0) {
      const isNoConn = cellsList.find(c => c.cell_id === selectedCell)?.name === 'no-conn';
      const computedName = isNoConn ? 'no-conn' : getNextInstanceName();
      setInstanceNameWithValidation(computedName);
    }
  }, [instanceName]);

  // Fetch libraries when dialog opens
  useEffect(() => {
  //   if (!open) {
  //   setLibrary('');
  //   setCellsList([]);
  //   setSelectedCell('');
  //   setSelectedView(null);
  //   setPopupInstanceName('');
  //   setInstanceNameError('');
  //   onInstanceNameValidity?.(true);
  //   setViewsList([]);
  // }
    if (open) {
      fetchLibraries();
    }
  }, [open]);

  // Fetch cells when a library is selected
 useEffect(() => {
  if (library) {
    fetchCells();
    if (!isEdit) {
      setSelectedCell('');
      setSelectedView(null);
      onCellViewSelect?.(null);
      setViewsList([]);
      setPopupInstanceName('');
      setInstanceNameError('');
      onInstanceNameValidity?.(true);
    }
  }
}, [library]);

useEffect(() => {
  const fetchViewForEdit = async () => {
    if (!isEdit || !selectedCell || !library || !cellsList.length) return;

    const cellObj = cellsList.find(c => c.name === selectedCell);
    if (!cellObj) return;

    try {
      const response = await getViewsByCellId(cellObj.cell_id);
      if(!response.success) return;
      const data = response.body;
      const matchedView = data.find(v => v.view_type_id === viewTypeId);
      setSelectedView(matchedView || null);      
    } catch (err) {
      console.error("Failed to fetch view for edit mode:", err);
    }
  };

  fetchViewForEdit();
}, [isEdit, selectedCell, library, cellsList, viewTypeId]);
  const fetchLibraries = async () => {
    try {
      let fetchPredefined = true
      const userId = localStorage.getItem('user_id');
      const response = await getLibraryByUserId(userId, fetchPredefined);
      if(!response.success) return;
    
      const data = response.body;
      if (viewTypeId === VIEW_TYPES.stick) {
        const filtered = data.filter(lib => lib.name.toLowerCase() === PREDEFINED_LIBRARIES[0]);
        setLibraries(filtered);
      } 
      else {
        setLibraries(data || []);
      }
    } catch (error) {
      console.error('Error fetching libraries:', error);
    }
  };

  const fetchCells = async () => {
    if (!library) return;

    try {
      const response = await getCellsByLibIdAndViewType(library, viewType);
      if(!response.success) return;      
      var data = response.body;
      setCellsList(data);
    } catch (error) {
      console.error('Error fetching cells:', error);
    }
  };

  const handleCellChange = async (event) => {
    const cellId = event.target.value;
    setSelectedCell(cellId);
    setViewsList([]);
    setSelectedView(null);

    const noConnection = cellsList.find(c => c.cell_id === cellId).name === 'no-conn';
    const computedName = noConnection ? 'no-conn' : getNextInstanceName();
    setInstanceNameWithValidation(computedName);
    
    const cellObj = cellsList.find(c => c.cell_id === cellId);
    // if (!cellObj) return;

    try {
      let views;
      if (activeTabCellId === cellObj.cell_id) {
        setViewsList([]);
      } else {
      const response = await getViewsByViewType(cellObj.cell_id, [viewType]);
      if(!response.success) return;
      views = response.body;

        setViewsList(views);
      }
      let matchedView = null;
      if (viewType === VIEW_TYPES.symbol) {
        matchedView = views.find(v => v.name === 'symbol');
      } else if (viewType === VIEW_TYPES.stick) {
        matchedView = views.find(v => v.name === 'stick');
      }

      const defaultView = matchedView || views[0];
      setSelectedView(defaultView);

      
      handleViewChange(defaultView, cellId);
    } catch (error) {
      console.error('Error fetching views:', error);
    }
  };

  const handleViewChange = (selView, cellId) => {    
    if (selView) {
      setSelectedView(selView);
      triggerCellViewSelect(selView.view_id, cellId);      
    }
  };

  const triggerCellViewSelect = (viewId, cellId) => { //cellId will remain null when dropdown is changed by user.

    const libName = libraries.find(lib => lib.library_id === library)?.name.toLowerCase();
    const isPredefined = PREDEFINED_LIBRARIES.includes(libName);
    const cellKey = isPredefined ? (cellsList.find(c => c.cell_id === cellId).name): 'symbol';
    const selected = cells.find(c => c.cell === cellKey);

    if (selected) {
      selected.cellName = cellsList.find(c => c.cell_id === cellId)?.name;
      onCellViewSelect?.({
        shapeType: selected.cell,
        imagePath: selected.path,
        libraryId: library,
        cellId: cellId, //this is null in case when selected from dropdown. Needs to be addressed.
        viewId: viewId,
        label: selected.cellName,
      });
    }
  }
  
  const handleClose = () => {
    setLibrary('');
    setCellsList([]);
    setSelectedCell('');
    setSelectedView(null);
    setSelectedCell('');
    setPopupInstanceName('');
    setInstanceNameError('');
    onInstanceNameValidity?.(true);
    onCellViewSelect?.(null); // <-- Clear symbol preview in parent
    setViewsList([]);
    onClose();
  };
  
  const handleHide = () => {
    onHide();
  };
  return (
    <CommonDialog
      open={open} onClose={handleClose} fullWidth maxWidth="xs" title={isEdit ? 'Edit Instance' : 'Add Instance'} hideBackDrop={true}>
        <>
          <TextField select fullWidth label="Library Name" value={library}  margin="normal" onChange={(e) => setLibrary(e.target.value)} disabled={isEdit}>
            {libraries.map(lib => (
              <MenuItem key={lib.library_id} value={lib.library_id}>
                {lib.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField select fullWidth label="Cell Name" margin="normal" value={selectedCell} onChange={handleCellChange} disabled={isEdit}>
            {cellsList.length === 0 ? (
              <MenuItem disabled>
                No cells available for selected library
              </MenuItem>
            ) : (
              cellsList.map(cell => (
                <MenuItem key={cell.cell_id} value={cell.cell_id}>
                  {cell.name}
                </MenuItem>
              ))
            )}
          </TextField>
          <TextField fullWidth select label="View Name" margin="normal" value={selectedView?.view_id || ''} disabled={isEdit}
            onChange={(e) => {
              const viewObj = viewsList.find(v => v.view_id === e.target.value);
              handleViewChange(viewObj);
            }}>
          {viewsList.length === 0 ? (
            <MenuItem disabled>
              No views available for selected cell
            </MenuItem>
          ) : (
            viewsList.map(v => (
              <MenuItem key={v.view_id} value={v.view_id}>
                {v.name}
              </MenuItem>
            ))
          )}
          </TextField>
          <TextField
            fullWidth
            margin="normal"
            label="Instance Name (Optional)"
            value={popupIstanceName}
            onChange={(e) => {
              setInstanceNameWithValidation(e.target.value);
            }}
            error={!!instanceNameError}
            helperText={instanceNameError}
            inputProps={{ maxLength: 6 }}
          />
        </>
        <>
        <Button variant="contained" className="customButtonStyle" onClick={handleClose}>Cancel</Button>
        <Button variant="contained" className="customButtonStyle" onClick={handleHide}>Hide</Button>
        </>
    </CommonDialog>
  );
};

export default AddInstanceDialog;
